<template>
  <div
    class="w-full flex flex-col justify-center mt-0 md:mt-[58px] md:mb-20"
    :class="{ 'thankyou-header': isThankYouPage }"
  >
    <div
      v-if="hasHeader"
      class="event-info flex flex-row md:justify-center justify-start gap-6 md:gap-0 thankyou-container"
    >
      <img
        :src="getImageUrl(event)"
        class="event-info__img lazyload thankyou-container__img"
        alt="userProfile"
        @error="
          $event.target.src = require('@/assets/images/placeholder-event.svg')
        "
      />
      <div
        class="flex flex-col items-start md:justify-between md:ml-8 thankyou-container__info"
      >
        <h2
          class="order-1 md:mt-[-8px]"
          :style="isIframe ? getPersonalizedEventTitle : ''"
        >
          {{ event.title }}
        </h2>
        <div
          class="flex flex-col md:order-2 order-3 items-start thankyou-container-venue"
        >
          <h3
            :style="getPersonalizedTitle"
            :class="[
              route.name === 'ThankYouPage' ? 'thankyou-venue' : '',
              route.name === 'PurchaseCrossingPage' &&
              route.fullPath.includes('getIn')
                ? 'text-black crossing-subtitle'
                : 'md:text-topaz-light text-grayInputBorder md:text-[20px] text-[15px]'
            ]"
          >
            {{ event.venue?.name }}
          </h3>
          <span
            v-if="event.venue?.address"
            :style="getPersonalizedVenueTitle"
            class="subtitle md:mt-2 md:block hidden thankyou-venue-location"
          >
            {{ event.venue?.address }}
          </span>
        </div>
        <div
          class="md:order-3 order-2"
          :class="[
            route.name === 'ThankYouPage' ? 'text-color-white' : '',
            route.name === 'PurchaseCrossingPage' &&
            route.fullPath.includes('getIn')
              ? 'text-grayInputBorder'
              : 'text-topaz-light md:text-color-white'
          ]"
        >
          <p
            v-if="!event.hide_start_date_on_ticket_office"
            :style="getPersonalizedDateTitle"
            :class="
              (getPersonalizedDateTitle && route.fullPath.includes('getIn')) ||
              route.fullPath.includes('widget')
                ? 'font-xs'
                : 'wegow-header-date'
            "
          >
            {{ formatDate(event.start_date, selectedRegion()) }}
          </p>
        </div>
      </div>
    </div>
    <div
      v-if="showSteps && hasSteps"
      class="sections"
      :class="
        isThankYouPage ? 'thankyou-timeline' : 'md:block hidden mt-12 md:mt-20'
      "
    >
      <div
        class="steps flex items-center justify-center relative md:w-[365px] mx-auto"
      >
        <time-line
          :steps="steps"
          :selected-section="selectedSection"
          :steps-color="getPersonalizedSteps.backgroundColor"
          :background="timeLineBg"
        ></time-line>
      </div>
      <p
        v-if="![5665726, 6011140].includes(event.id) && !letDownload"
        class="text-center topaz mt-6"
        :style="{ color: getPersonalizedSteps.backgroundColor }"
      >
        <span v-if="purchaseData.has_cross_sell">
          {{
            $t(
              "wegow.purchase.crossingPurchaseTitle.stepTitle" +
                (selectedSection + 1)
            )
          }}
        </span>
        <span v-else>
          {{ $t("wegow.purchase.stepTitle" + (selectedSection + 1)) }}
        </span>
      </p>
      <p
        v-if="maxNumberTicketByCode > -1 && selectedSection === 0"
        class="malva flex justify-center mt-4"
      >
        {{
          $t("wegow.purchase.remainigTotal", {
            remainingTickets: maxNumberTicketByCode
          })
        }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { useEvent } from "@/composables/useEvent";
import { useIframeStyle } from "@/composables/useIframeStyle";
import { usePurchase } from "@/composables/usePurchase";
import { useUser } from "@/composables/useUser";
import { useRoute } from "vue-router";
import { computed, ref, onMounted } from "vue";

import { formatDayWeekDate } from "@/plugins/date-formater";
import TimeLine from "@/components/general/ui/TimeLine.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { userDetail, selectedRegion } = useUser();
const { getPersonalizedTitle } = useIframeStyle();
const route = useRoute();

const formatDate = (date, locale) => {
  let infoDate = formatDayWeekDate(date, locale);
  let dayCap =
    infoDate.dayweek.charAt(0).toUpperCase() + infoDate.dayweek.slice(1);
  return t("profiles.startWithoutHour", {
    weekday: dayCap.slice(0, 3),
    monthday: infoDate.day,
    dayformat: selectedRegion() === "en" ? infoDate.sufix : "",
    month: infoDate.month,
    year: infoDate.year,
    time: infoDate.hour
  });
};

const { details: event, isIframe, iframeStyles } = useEvent();
const {
  getPersonalizedEventTitle,
  getPersonalizedVenueTitle,
  getPersonalizedDateTitle,
  getPersonalizedAuxColor,
  getPersonalizedSteps
} = useIframeStyle();

const { maxNumberTicketByCode, purchaseData } = usePurchase();

const props = defineProps({
  selectedSection: {
    type: Number,
    default: 0
  },
  showSteps: {
    type: Boolean,
    default: true
  }
});
const basicSteps = ref([
  {
    id: 1,
    name: t("wegow.purchase.stepTitle1"),
    selected: props.selectedSection + 1 === 1
  },
  {
    id: 2,
    name: t("wegow.purchase.stepTitle2"),
    selected: props.selectedSection + 1 === 2
  },
  {
    id: 3,
    name: t("wegow.purchase.stepTitle3"),
    selected: props.selectedSection + 1 === 3
  }
]);
const crossingStep = ref({
  id: 2,
  name: t("wegow.purchase.stepTitle2"),
  selected: props.selectedSection + 1 === 2
});

const timeLineBg = computed(() => {
  if (iframeStyles.value.background_color) {
    return iframeStyles.value.background_color;
  } else {
    return "#000214";
  }
});

const letDownload = computed(() => {
  return (
    route.name === "ThankYouPage" &&
    purchaseData.value.hide_download_button === true
  );
});
const hasCrossingSell = computed(() => {
  return purchaseData.value.has_cross_sell;
});

const steps = computed(() => {
  let steps = basicSteps.value;
  if (hasCrossingSell.value) {
    steps = steps.map(step => {
      if (step.id >= 2) {
        return {
          ...step,
          id: step.id + 1,
          selected: props.selectedSection + 1 === step.id + 1
        };
      }
      return step;
    });
    addStep(steps, 1, crossingStep.value);
  }
  return steps;
});

const hasSteps = computed(() => {
  if (route.query.steps) {
    return route.query.steps === "true";
  } else {
    return iframeStyles.value.steps !== "false";
  }
});
const hasHeader = computed(() => {
  if (route.query.header) {
    return route.query.header === "true";
  } else {
    return iframeStyles.value.header === false
      ? iframeStyles.value.header
      : true;
  }
});
const isThankYouPage = computed(() => {
  return route.name === "ThankYouPage";
});

const getImageUrl = element => {
  return (
    element.image_url ||
    element.thumbnails?.s_hor_dt ||
    element.thumbnails ||
    require("@/assets/images/placeholder-event.svg")
  );
};
const addStep = (steps, position, step) => {
  steps.splice(position, 0, step);
};
</script>

<style lang="scss" scoped>
// h2 {
//   font-weight: 600;
//   font-size: 30px;
//   line-height: 36px;
// }
// h3 {
//   font-weight: 400;
//   font-size: 20px;
//   line-height: 30px;
//   &.font-xs {
//     font-size: 14px;
//   }
// }
.thankyou-container__info {
  margin-bottom: 12px;
}
@media screen and (max-width: $screen-md) {
  .thankyou-header {
    padding: 0;
    .thankyou-container {
      flex-direction: column;
      gap: 18px;
      margin-top: 40px;
      .thankyou-container__img {
        width: 100px;
        height: 100px;
        border-radius: 15px;
        margin: auto;
      }
      .thankyou-container__info {
        padding: 0 24px;
        align-items: center;
        h2,
        h3 {
          font-size: 15px;
          text-align: center;
          line-height: normal;
        }
        h3 {
          font-size: 1rem;
        }
        div {
          margin: auto;
        }
        .thankyou-venue {
          text-align: center;
        }
        .thankyou-venue-location {
          display: inline-block;
          font-size: 14px;
          text-align: center;
          margin: auto;
        }
        .thankyou-container-venue {
          align-items: center;
          margin-bottom: 1rem;
          order: 1;
        }
      }
    }
    .thankyou-timeline {
      margin-top: 21px;
      margin-bottom: 31px;
    }
  }
}
.thankyou-timeline {
  margin-top: 5rem;
}
.thankyou-venue {
  color: $topaz;
}
.topaz {
  color: $topaz;
}
.malva {
  color: $malva;
}
.subtitle {
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: $textSubtitle;
}

.event-info {
  padding-bottom: 21px;
  &__img {
    width: 100px;
    height: 100%;
    aspect-ratio: 1/1;
    border-radius: 15px;
    object-fit: cover;
  }
  h2 {
    font-size: 1rem;
    font-weight: 600;
    line-height: normal;
    text-align: start;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: start;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 14.5px;
  }
  p {
    &.font-xs {
      font-size: 12px;
      color: #8d8d8d;
    }
  }
}
.crossing-subtitle {
  font-size: 12px;
}
@media screen and (min-width: $screen-md) {
  .event-info {
    &__img {
      width: 173px;
      height: 173px;
      aspect-ratio: 1/1;
      border-radius: 5px;
      object-fit: cover;
    }
    h2 {
      font-size: 30px;
      margin-bottom: 26px;
    }
    p {
      font-size: 25px;
      &.font-xs {
        font-size: 14px;
      }
    }
    p {
      font-size: 14px;
    }
    .wegow-header-date {
      font-size: 20px;
    }
  }
  .crossing-subtitle {
    font-size: 25px;
  }
}
</style>
